import { uuid25 } from "../../../../uuid/dist/uuid25.js";
import { ColumnOption, OrderType } from "../../../../constants/schema.js";
import { addColumnAction, changeColumnAutoIncrementAction, changeColumnCommentAction, changeColumnDataTypeAction, changeColumnDefaultAction, changeColumnDomainAction, changeColumnLogicalNameAction, changeColumnNameAction, changeColumnNameDerivedAction, changeColumnNotNullAction, changeColumnPrimaryKeyAction, changeColumnUniqueAction } from "../../table-column/atom.actions.js";
import { bHas } from "../../../../utils/bit.js";
import { addIndexAction, changeIndexNameAction, changeIndexUniqueAction } from "../../index/atom.actions.js";
import { addIndexColumnAction, changeIndexColumnOrderTypeAction } from "../../index-column/atom.actions.js";
import { addMemoAction, changeMemoValueAction } from "../../memo/atom.actions.js";
import { addRelationshipAction } from "../../relationship/atom.actions.js";
import { addTableAction, changeTableCommentAction, changeTableGroupAction, changeTableLogicalNameAction, changeTableNameAction, changeTableNameDerivedAction } from "../../table/atom.actions.js";
import { addTableGroupAction, changeTableGroupNameAction } from "../../table-group/atom.actions.js";
//#region src/engine/modules/editor/utils/duplicate.ts
function toCreateEntityActions({ tables, columns, memos, relationships, indexes, tableGroups = [] }, placement, { valuesOnly = false } = {}) {
	const actions = [];
	const set = (holds, action) => {
		(holds || !valuesOnly) && actions.push(action);
	};
	const carry = (holds, action) => {
		holds && actions.push(action);
	};
	const tableIds = [];
	const memoIds = [];
	const tableGroupIds = [];
	const columnBySourceId = new Map(columns.map((column) => [column.sourceId, column]));
	const tableIdBySourceId = /* @__PURE__ */ new Map();
	const columnIdBySourceId = /* @__PURE__ */ new Map();
	for (const table of tables) {
		const point = placement.get(table.sourceId);
		if (!point) continue;
		const tableId = uuid25();
		tableIds.push(tableId);
		tableIdBySourceId.set(table.sourceId, tableId);
		actions.push(addTableAction({
			id: tableId,
			ui: {
				x: point.x,
				y: point.y,
				zIndex: point.zIndex,
				color: table.ui.color
			}
		}));
		set(!!table.name, changeTableNameAction({
			id: tableId,
			value: table.name
		}));
		carry(!!table.logicalName, changeTableLogicalNameAction({
			id: tableId,
			value: table.logicalName ?? ""
		}));
		set(!!table.comment, changeTableCommentAction({
			id: tableId,
			value: table.comment
		}));
		carry(table.nameDerived === true, changeTableNameDerivedAction({
			id: tableId,
			value: true
		}));
		for (const sourceColumnId of table.columnIds) {
			const column = columnBySourceId.get(sourceColumnId);
			if (!column) continue;
			const payload = {
				id: uuid25(),
				tableId
			};
			columnIdBySourceId.set(sourceColumnId, payload.id);
			const primaryKey = bHas(column.options, ColumnOption.primaryKey);
			const notNull = bHas(column.options, ColumnOption.notNull);
			const unique = bHas(column.options, ColumnOption.unique);
			const autoIncrement = bHas(column.options, ColumnOption.autoIncrement);
			actions.push(addColumnAction(payload));
			set(!!column.name, changeColumnNameAction({
				...payload,
				value: column.name
			}));
			carry(!!column.logicalName, changeColumnLogicalNameAction({
				...payload,
				value: column.logicalName ?? ""
			}));
			set(!!column.dataType, changeColumnDataTypeAction({
				...payload,
				value: column.dataType
			}));
			carry(!!column.domainId, changeColumnDomainAction({
				...payload,
				value: column.domainId ?? ""
			}));
			set(!!column.default, changeColumnDefaultAction({
				...payload,
				value: column.default
			}));
			set(!!column.comment, changeColumnCommentAction({
				...payload,
				value: column.comment
			}));
			set(primaryKey, changeColumnPrimaryKeyAction({
				...payload,
				value: primaryKey
			}));
			set(notNull, changeColumnNotNullAction({
				...payload,
				value: notNull
			}));
			set(unique, changeColumnUniqueAction({
				...payload,
				value: unique
			}));
			set(autoIncrement, changeColumnAutoIncrementAction({
				...payload,
				value: autoIncrement
			}));
			carry(bHas(column.options, ColumnOption.nameDerived), changeColumnNameDerivedAction({
				...payload,
				value: true
			}));
		}
	}
	for (const relationship of relationships) {
		const startTableId = tableIdBySourceId.get(relationship.start.tableId);
		const endTableId = tableIdBySourceId.get(relationship.end.tableId);
		if (!startTableId || !endTableId) continue;
		const startColumnIds = toNewColumnIds(relationship.start.columnIds, columnIdBySourceId);
		const endColumnIds = toNewColumnIds(relationship.end.columnIds, columnIdBySourceId);
		if (!startColumnIds || !endColumnIds) continue;
		actions.push(addRelationshipAction({
			id: uuid25(),
			relationshipType: relationship.relationshipType,
			onDelete: relationship.onDelete,
			onUpdate: relationship.onUpdate,
			start: {
				tableId: startTableId,
				columnIds: startColumnIds
			},
			end: {
				tableId: endTableId,
				columnIds: endColumnIds
			}
		}));
	}
	for (const index of indexes) {
		const tableId = tableIdBySourceId.get(index.tableId);
		if (!tableId) continue;
		const columnIds = toNewColumnIds(index.indexColumns.map(({ columnId }) => columnId), columnIdBySourceId);
		if (!columnIds) continue;
		const indexId = uuid25();
		actions.push(addIndexAction({
			id: indexId,
			tableId
		}));
		set(!!index.name, changeIndexNameAction({
			id: indexId,
			tableId,
			value: index.name
		}));
		set(index.unique, changeIndexUniqueAction({
			id: indexId,
			tableId,
			value: index.unique
		}));
		index.indexColumns.forEach(({ orderType }, i) => {
			const id = uuid25();
			const columnId = columnIds[i];
			actions.push(addIndexColumnAction({
				id,
				indexId,
				tableId,
				columnId
			}));
			set(orderType !== OrderType.ASC, changeIndexColumnOrderTypeAction({
				id,
				indexId,
				columnId,
				value: orderType
			}));
		});
	}
	for (const memo of memos) {
		const point = placement.get(memo.sourceId);
		if (!point) continue;
		const memoId = uuid25();
		memoIds.push(memoId);
		actions.push(addMemoAction({
			id: memoId,
			ui: {
				x: point.x,
				y: point.y,
				zIndex: point.zIndex,
				color: memo.ui.color,
				width: memo.ui.width,
				height: memo.ui.height
			}
		}));
		set(!!memo.value, changeMemoValueAction({
			id: memoId,
			value: memo.value
		}));
	}
	for (const group of tableGroups) {
		const tableGroupId = uuid25();
		tableGroupIds.push(tableGroupId);
		actions.push(addTableGroupAction({
			id: tableGroupId,
			color: group.color,
			ui: { ...group.ui }
		}));
		set(!!group.name, changeTableGroupNameAction({
			id: tableGroupId,
			value: group.name
		}));
		for (const sourceTableId of group.tableIds) {
			const tableId = tableIdBySourceId.get(sourceTableId);
			if (!tableId) continue;
			actions.push(changeTableGroupAction({
				id: tableId,
				value: tableGroupId
			}));
		}
	}
	return {
		actions,
		tableIds,
		memoIds,
		tableGroupIds
	};
}
function toNewColumnIds(sourceColumnIds, columnIdBySourceId) {
	const columnIds = [];
	for (const sourceColumnId of sourceColumnIds) {
		const columnId = columnIdBySourceId.get(sourceColumnId);
		if (!columnId) return null;
		columnIds.push(columnId);
	}
	return columnIds;
}
//#endregion
export { toCreateEntityActions };
